import { useCallback, useEffect, useState } from 'react';
import type { AdminWhatsappSystemInstance } from '../../../domain/superadmin/types';
import { useAppContext } from '../../../contexts/AppContext';
import {
  connectAdminWhatsappSystemInstance,
  disconnectAdminWhatsappSystemInstance,
  fetchAdminWhatsappSystemInstance,
  reconnectAdminWhatsappSystemInstance,
} from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';

export function useAdminWhatsappSystemInstance() {
  const [instance, setInstance] = useState<AdminWhatsappSystemInstance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setCurrentPage } = useAppContext();

  const handleForbidden = useCallback(() => {
    setCurrentPage('dashboard');
    notify.warning('Acesso não autorizado à área de superadmin.');
  }, [setCurrentPage]);

  const load = useCallback(
    async (options?: { silent?: boolean }): Promise<AdminWhatsappSystemInstance | null> => {
      const silent = options?.silent ?? false;
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);
      try {
        const result = await fetchAdminWhatsappSystemInstance();
        setInstance(result);
        return result;
      } catch (e) {
        if (e instanceof SuperadminApiError && e.isForbidden) {
          handleForbidden();
          return;
        }
        setError(e instanceof Error ? e.message : 'Erro ao carregar instância do sistema');
        return null;
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
      }
    },
    [handleForbidden],
  );

  const pollStatus = useCallback(() => load({ silent: true }), [load]);

  const connect = useCallback(async (): Promise<AdminWhatsappSystemInstance | undefined> => {
    setIsMutating(true);
    setError(null);
    try {
      const result = await connectAdminWhatsappSystemInstance();
      setInstance(result);
      return result;
    } catch (e) {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        handleForbidden();
        return;
      }
      notify.error(e instanceof Error ? e.message : 'Falha ao conectar instância');
      await load();
      return undefined;
    } finally {
      setIsMutating(false);
    }
  }, [handleForbidden, load]);

  const reconnect = useCallback(async (): Promise<AdminWhatsappSystemInstance | undefined> => {
    setIsMutating(true);
    setError(null);
    try {
      const result = await reconnectAdminWhatsappSystemInstance();
      setInstance(result);
      return result;
    } catch (e) {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        handleForbidden();
        return;
      }
      notify.error(e instanceof Error ? e.message : 'Falha ao reconectar instância');
      await load();
      return undefined;
    } finally {
      setIsMutating(false);
    }
  }, [handleForbidden, load]);

  const disconnect = useCallback(async () => {
    setIsMutating(true);
    setError(null);
    try {
      const result = await disconnectAdminWhatsappSystemInstance();
      setInstance(result);
    } catch (e) {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        handleForbidden();
        return;
      }
      notify.error(e instanceof Error ? e.message : 'Falha ao desconectar instância');
      await load();
    } finally {
      setIsMutating(false);
    }
  }, [handleForbidden, load]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    instance,
    isLoading,
    error,
    refetch: load,
    pollStatus,
    connect,
    reconnect,
    disconnect,
    isMutating,
  };
}
